import React, { useState } from 'react';
import ProjectInformationModal from '../ProjectInformationModal';

export default function Projects({ projects }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  // Helper function to get status badge color
  const getStatusBadgeColor = status => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-600";
      case "inprogress":
        return "bg-[#0258DA]";
      case "planning":
        return "bg-yellow-500";
      case "cancelled":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  // Helper function to get background color based on status
  const getBackgroundColor = status => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-green-100";
      case "inprogress":
        return "bg-blue-100";
      case "planning":
        return "bg-yellow-100";
      case "cancelled":
        return "bg-red-100";
      default:
        return "bg-gray-100";
    }
  };

  // Helper function to get icon color based on status
  const getIconColor = status => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-600";
      case "inprogress":
        return "text-[#0258DA]";
      case "planning":
        return "text-yellow-600";
      case "cancelled":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  const getProjectImage = (project) => {
    if (project?.image) return project.image;
    if (Array.isArray(project?.images) && project.images.length > 0) {
      return project.images[0]; // show first image
    }
    return null;
  };

  // Helper function to get client initials
  const getClientInitials = client => {
    if (!client) return "NA";
    return client
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper function to parse services into array
  const parseServices = services => {
    if (!services) return [];
    return services.split(",").map(service => service.trim());
  };

  // Fixed team member initials for consistent display
  const teamInitials = ["MK", "JD", "AL"];

  // Handle modal open
  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  if (!projects || projects.length === 0) {
    return (
      <section
        className="mb-16"
        id="projects"
      >
        <h2 className="text-3xl font-bold mb-8 text-[#0258DA]">Current Projects</h2>
        <p className="text-center text-gray-500">No projects available at the moment.</p>
      </section>
    );
  }

  // Split projects into featured (first 2) and regular (rest)
  const featuredProjects = projects.slice(0, 2);
  const regularProjects = projects.slice(2);

  return (
    <section
      className="mb-16"
      id="projects"
    >
      <h2 className="text-3xl font-bold mb-8 text-[#0258DA]">Current Projects</h2>

      {/* Featured Projects Grid */}
      {featuredProjects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {featuredProjects.map(project => (
            <div
              key={project._id}
              className="bg-white rounded-lg shadow-md overflow-hidden card"
            >
              <div className="h-64 relative overflow-hidden">
                {getProjectImage(project) ? (
                  <img
                    src={getProjectImage(project)}
                    alt={project.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className={`w-full h-full ${getBackgroundColor(project.status)} relative`}>
                    <div
                      className={`absolute inset-0 flex items-center justify-center ${getIconColor(project.status)} opacity-20`}
                    >
                      <svg
                        className="w-32 h-32"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                )}

                <span
                  className={`absolute top-4 right-4 ${getStatusBadgeColor(project.status)} text-white text-xs font-bold px-3 py-1 rounded-full`}
                >
                  {project.status}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2 text-[#0258DA]">{project.name}</h3>
                <p className="text-[#465362] mb-4">{project.desc}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {parseServices(project.services).map((service, index) => (
                    <span
                      key={index}
                      className="bg-blue-100 text-[#0258DA] text-xs font-medium px-3 py-1 rounded-full"
                    >
                      {service}
                    </span>
                  ))}
                </div>

                <div className="flex justify-between items-center">
                  {/*<div className="flex items-center">*/}
                  {/*  <div className="w-8 h-8 rounded-full bg-[#0258DA] flex items-center justify-center text-white font-bold text-xs">*/}
                  {/*    {getClientInitials(project.client)}*/}
                  {/*  </div>*/}
                  {/*  {teamInitials.map((initial, index) => (*/}
                  {/*    <div key={index} className={`w-8 h-8 rounded-full ${index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-blue-400' : 'bg-blue-300'} flex items-center justify-center text-white font-bold text-xs -ml-2`}>*/}
                  {/*      {initial}*/}
                  {/*    </div>*/}
                  {/*  ))}*/}
                  {/*</div>*/}
                  <button
                    onClick={() => handleOpenModal(project)}
                    className="text-[#0258DA] font-medium hover:underline cursor-pointer"
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Regular Projects Grid */}
      {regularProjects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          {regularProjects.map(project => (
            <div
              key={project._id}
              className="bg-white rounded-lg shadow-md overflow-hidden card"
            >
              <div className={`h-48 ${getBackgroundColor(project.status)} relative`}>
                <span
                  className={`absolute top-4 right-4 ${getStatusBadgeColor(project.status)} text-white text-xs font-bold px-3 py-1 rounded-full`}
                >
                  {project.status}
                </span>
                <div
                  className={`absolute inset-0 flex items-center justify-center ${getIconColor(project.status)} opacity-20`}
                >
                  <svg
                    className="w-24 h-24"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                  </svg>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold mb-2 text-[#0258DA]">{project.name}</h3>
                <p className="text-[#465362] mb-4">{project.desc}</p>
                <button
                  onClick={() => handleOpenModal(project)}
                  className="text-[#0258DA] font-medium hover:underline cursor-pointer"
                >
                  Learn More →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-center mt-12">
        <a
          href="/projects"
          className="btn-outline"
        >
          View All Projects
        </a>
      </div>

      {/* Project Information Modal */}
      <ProjectInformationModal
        open={modalOpen}
        onClose={handleCloseModal}
        project={selectedProject}
      />
    </section>
  );
}
